import { FC, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { TruncatedText } from "~/components/ui/truncated-text";
import {
  DuplicateMatch,
  IntraBatchDuplicate,
} from "~/features/case/hooks/useDocumentsList";
import { trackEvent } from "~/utils/analytics";

type Props = {
  isOpen: boolean;
  duplicateFiles: string[];
  duplicateMatches: DuplicateMatch[];
  intraBatchDuplicates: IntraBatchDuplicate[];
  pendingFiles: File[];
  onConfirm: () => void;
  onUploadUniqueOnly: () => void;
  onCancel: () => void;
};

export const DuplicateFilesModal: FC<Props> = ({
  isOpen,
  duplicateFiles,
  duplicateMatches,
  intraBatchDuplicates,
  pendingFiles,
  onConfirm,
  onUploadUniqueOnly,
  onCancel,
}) => {
  const hasInterBatch = duplicateMatches.length > 0;
  const hasIntraBatch = intraBatchDuplicates.length > 0;

  const intraBatchExtraCount = useMemo(() => {
    return intraBatchDuplicates.reduce(
      (sum, group) => sum + group.files.length - 1,
      0
    );
  }, [intraBatchDuplicates]);

  const totalSkippedCount = duplicateFiles.length + intraBatchExtraCount;
  const totalFileCount = pendingFiles.length;
  const uniqueFileCount = totalFileCount - totalSkippedCount;

  useEffect(() => {
    if (
      isOpen &&
      (duplicateFiles.length > 0 || intraBatchDuplicates.length > 0)
    ) {
      trackEvent({
        eventName: "app/case/duplicate_modal.modal/shown",
        properties: {
          interBatchCount: duplicateFiles.length,
          intraBatchGroupCount: intraBatchDuplicates.length,
          intraBatchExtraCount,
          totalFileCount: pendingFiles.length,
          duplicateFilenames: duplicateFiles,
        },
      });
    }
  }, [
    isOpen,
    duplicateFiles,
    intraBatchDuplicates,
    intraBatchExtraCount,
    pendingFiles,
  ]);

  return (
    <Dialog open={isOpen} onOpenChange={() => onCancel()}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="documentsList.duplicateFiles.titleContentMatch"
              defaultMessage="Duplicate Content Detected"
            />
          </DialogTitle>
          <DialogDescription>
            {hasInterBatch && !hasIntraBatch ? (
              <FormattedMessage
                id="documentsList.duplicateFiles.partialDuplicateDescription"
                defaultMessage="{duplicateCount, plural, one {# file has} other {# files have}} identical content to documents already in this case. {totalCount, plural, one {The file} other {All # files}} will be uploaded."
                values={{
                  duplicateCount: totalSkippedCount,
                  totalCount: totalFileCount,
                }}
              />
            ) : !hasInterBatch && hasIntraBatch ? (
              <FormattedMessage
                id="documentsList.duplicateFiles.intraBatchOnlyDescription"
                defaultMessage="{duplicateCount, plural, one {# file has} other {# files have}} identical content to other files in this upload. {totalCount, plural, one {The file} other {All # files}} will be uploaded."
                values={{
                  duplicateCount: totalSkippedCount,
                  totalCount: totalFileCount,
                }}
              />
            ) : (
              <FormattedMessage
                id="documentsList.duplicateFiles.mixedDescription"
                defaultMessage="{duplicateCount, plural, one {# file has} other {# files have}} duplicate content. {totalCount, plural, one {The file} other {All # files}} will be uploaded."
                values={{
                  duplicateCount: totalSkippedCount,
                  totalCount: totalFileCount,
                }}
              />
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 max-h-[60vh] space-y-4 overflow-y-auto rounded border border-border p-2">
          {hasInterBatch && (
            <div>
              <p className="mb-2 text-sm font-medium">
                <FormattedMessage
                  id="documentsList.duplicateFiles.duplicateFilesLabel"
                  defaultMessage="{duplicateCount, plural, one {File matching existing document:} other {Files matching existing documents:}}"
                  values={{ duplicateCount: duplicateFiles.length }}
                />
              </p>
              <div className="space-y-2">
                {duplicateMatches.map((match) => (
                  <div
                    key={match.newFileName}
                    className="overflow-hidden space-y-1 rounded-md bg-muted/50 p-2"
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        <FormattedMessage
                          id="documentsList.duplicateFiles.newLabel"
                          defaultMessage="New"
                        />
                      </span>
                      <TruncatedText
                        text={match.newFileName}
                        className="min-w-0 flex-1 text-sm font-medium text-foreground"
                      />
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 pl-0.5">
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {"\u2192"}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        <FormattedMessage
                          id="documentsList.duplicateFiles.matchesExisting"
                          defaultMessage="matches"
                        />
                      </span>
                      <TruncatedText
                        text={match.existingFileName}
                        className="min-w-0 flex-1 text-xs text-muted-foreground"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasIntraBatch && (
            <div>
              <p className="mb-2 text-sm font-medium">
                <FormattedMessage
                  id="documentsList.duplicateFiles.intraBatchLabel"
                  defaultMessage="Files with identical content in this upload:"
                />
              </p>
              <div className="space-y-2">
                {intraBatchDuplicates.map((group) => (
                  <div
                    key={group.hash}
                    className="overflow-hidden space-y-1 rounded-md bg-muted/50 p-2"
                  >
                    {group.files.map((file) => (
                      <div
                        key={file.name}
                        className="flex min-w-0 items-center gap-1.5"
                      >
                        <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                          <FormattedMessage
                            id="documentsList.duplicateFiles.duplicateBadge"
                            defaultMessage="Duplicate"
                          />
                        </span>
                        <TruncatedText
                          text={file.name}
                          className="min-w-0 flex-1 text-sm font-medium text-foreground"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            onClick={() => {
              trackEvent({
                eventName: "app/case/duplicate_modal/cancel.button/click",
                properties: {
                  interBatchCount: duplicateFiles.length,
                  intraBatchExtraCount,
                  totalFileCount: pendingFiles.length,
                },
              });
              onCancel();
            }}
            variant="ghost"
          >
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          {uniqueFileCount > 0 && (
            <Button
              variant="outline"
              onClick={() => {
                trackEvent({
                  eventName:
                    "app/case/duplicate_modal/upload_unique.button/click",
                  properties: {
                    interBatchCount: duplicateFiles.length,
                    intraBatchExtraCount,
                    uniqueFileCount,
                  },
                });
                onUploadUniqueOnly();
              }}
            >
              <FormattedMessage
                id="documentsList.duplicateFiles.uploadUniqueOnly"
                defaultMessage="Upload {uniqueFileCount, plural, one {# Unique File} other {# Unique Files}}"
                values={{ uniqueFileCount }}
              />
            </Button>
          )}
          {totalFileCount > 0 && (
            <Button
              onClick={() => {
                trackEvent({
                  eventName: "app/case/duplicate_modal/upload_all.button/click",
                  properties: {
                    interBatchCount: duplicateFiles.length,
                    intraBatchExtraCount,
                    totalFileCount,
                  },
                });
                onConfirm();
              }}
            >
              <FormattedMessage
                id="documentsList.duplicateFiles.uploadAll"
                defaultMessage="Upload {totalFileCount, plural, one {# File} other {# Files}}"
                values={{ totalFileCount }}
              />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
